import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { HELP_ALERT } from './helpBlock';

/** A long label (« Enregistrer et prévenir 3 techniciens ») wraps instead of overflowing at 360 px. */
export const WRAPPING_BUTTON = 'h-auto min-h-9 whitespace-normal text-left';

export type CardSaveStatus = 'draft' | 'upToDate' | 'dirty' | 'saving' | 'saved' | 'jiraRefused';

interface CardSaveFooterProps {
  status: CardSaveStatus;
  /** `HH:mm`, for « Enregistré à {heure} ». */
  savedAt?: string;
  saveLabel?: string;
  saveHelp?: string;
  saveDisabled?: boolean;
  /** Already worded: the error stays in the card, never a toast (F6). */
  error?: string | null;
  onSave?: () => void;
  onCancel?: () => void;
}

/** One card's own save footer (F15, option A of 2b) — the Mission card and the skills card. */
const CardSaveFooter = ({
  status,
  savedAt,
  saveLabel,
  saveHelp,
  saveDisabled = false,
  error,
  onSave,
  onCancel,
}: CardSaveFooterProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 border-t border-zinc-200 pt-3 dark:border-zinc-800">
      {error && (
        <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
          {error}
        </p>
      )}
      {status === 'dirty' ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-amber-700 dark:text-amber-400">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              {t('Unsaved changes')}
            </span>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onCancel}>
                {t('Cancel the changes')}
              </Button>
              <Button type="button" variant="outline" className={WRAPPING_BUTTON} onClick={onSave} disabled={saveDisabled}>
                {saveLabel ?? t('Save changes')}
              </Button>
            </div>
          </div>
          {saveHelp && <p className="text-[12px] text-muted-foreground">{saveHelp}</p>}
        </>
      ) : status === 'jiraRefused' ? (
        <p className={HELP_ALERT} role="alert">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t('Saved here, but Jira refused the change.')}
        </p>
      ) : (
        <p className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground" role="status">
          {status === 'saving' && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          {status === 'draft' && t('Saved when the solicitations are sent.')}
          {status === 'upToDate' && t('Up to date')}
          {status === 'saving' && t('Saving changes…')}
          {status === 'saved' && t('Saved at {{time}}', { time: savedAt })}
        </p>
      )}
    </div>
  );
};

export default CardSaveFooter;
